import React from "react";
import { Typography, Chip } from "@mui/material";
import { CHAT_REGEX, VIEWS, CHAT_MESSAGES } from "../consts.js";
import { CONVERSATION_TYPES } from "../../../../redux/chatSlice.js";

export const isFinalMessage = (item) =>
  (item.includes("You're now ready to fill in your answer") &&
    item.includes("Let me know when you're ready to discuss next question")) ||
  item.includes(
    "Well done! You've touched all major aspects of this question."
  );

export const reformatContent = (
  content,
  highlights,
  variation,
  questions,
  selectedQuestion,
  onAnswerClick,
  handleQuestionSelect,
  handleHighlightClick,
  setActiveMode
) => {
  if (!content || !highlights || variation === CONVERSATION_TYPES.OUTGOING)
    return content || null;
  let processedContent = content;
  // Apply processors in sequence
  processedContent = processHighlights(
    processedContent,
    highlights,
    handleHighlightClick,
    setActiveMode
  );
  processedContent = applyProcessing(processedContent, (item) =>
    processAnswers(item, isFinalMessage, onAnswerClick)
  );
  processedContent = applyProcessing(processedContent, (item) =>
    processNextQuestion(item, questions, selectedQuestion, handleQuestionSelect)
  );
  return processedContent;
};

export const getHighlightIndex = (uuid, highlights) =>
  highlights.findIndex((highlight) => highlight.id === uuid);

// Converts UUIDs supplied by the bot to the relevant clickable highlight chip
export const processHighlights = (
  content,
  highlights,
  handleHighlightClick,
  setActiveMode
) => {
  if (typeof content !== "string") return content;
  if (!content.match(CHAT_REGEX.UUID)) return content;
  const parts = content.split(CHAT_REGEX.UUID);
  const result = [];
  parts.forEach((part, index) => {
    if (part.match(CHAT_REGEX.UUID)) {
      const highlightIndex = getHighlightIndex(part, highlights);
      result.push(
        <Chip
          key={`uuid-${index}`}
          label={
            highlightIndex !== -1
              ? `Highlight ${highlightIndex + 1}`
              : "Highlight deleted"
          }
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: "24px", fontSize: "13px" }}
          onClick={() => {
            setActiveMode(VIEWS.READER);
            handleHighlightClick(highlightIndex);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setActiveMode(VIEWS.READER);
              handleHighlightClick(highlightIndex);
            }
          }}
        />
      );
    } else {
      result.push(part);
    }
  });
  return result;
};

// replace answer string with clickable button
export const processAnswers = (item, isFinalMessageFn, onAnswerClick) => {
  if (typeof item !== "string") return item;
  if (!CHAT_REGEX.ANSWER.test(item)) return item;
  if (!isFinalMessageFn(item)) return item;
  const parts = item.split(CHAT_REGEX.ANSWER);
  const matches = item.match(CHAT_REGEX.ANSWER);
  const result = [];
  parts.forEach((part, i) => {
    if (part) result.push(part);
    if (i < parts.length - 1) {
      result.push(
        <Typography
          key={`answer-${i}`}
          component="button"
          variant="body2"
          sx={{
            color: "inherit",
            textDecoration: "underline",
            cursor: "pointer",
            padding: 0,
            background: "none",
            border: "none",
            font: "inherit",
            textAlign: "left"
          }}
          onClick={onAnswerClick}>
          {matches[i]}
        </Typography>
      );
    }
  });
  return result;
};

// Convert "next question" string to button and handle special case for last question
export const processNextQuestion = (
  item,
  questions,
  selectedQuestion,
  handleQuestionSelect
) => {
  if (typeof item !== "string") return item;
  if (!CHAT_REGEX.NEXT_QUESTION.test(item)) return item;
  const isLastQuestion =
    questions &&
    selectedQuestion &&
    questions.indexOf(selectedQuestion) + 1 === questions.length;
  if (isLastQuestion) {
    item = item.replace(
      "Let me know when you're ready to discuss next question",
      CHAT_MESSAGES.LAST_QUESTION
    );
    item = item.replace(
      "When you're ready, we can discuss next question",
      CHAT_MESSAGES.LAST_QUESTION
    );
  }
  const parts = item.split(CHAT_REGEX.NEXT_QUESTION);
  const matches = item.match(CHAT_REGEX.NEXT_QUESTION);
  const result = [];
  parts.forEach((part, i) => {
    if (part) result.push(part);
    // replace "next question" string with clickable button in all appearances
    if (i < parts.length - 1) {
      result.push(
        <Typography
          key={`next-question-${i}`}
          component="button"
          sx={{
            color: "inherit",
            textDecoration: "underline",
            cursor: "pointer",
            padding: 0,
            background: "none",
            border: "none",
            font: "inherit"
          }}
          onClick={handleQuestionSelect}>
          {matches[i]}
        </Typography>
      );
    }
  });

  return result;
};

// applies a processor function to content
export const applyProcessing = (content, processor) => {
  if (Array.isArray(content)) {
    return content.flatMap((item) => {
      if (Array.isArray(item)) {
        return applyProcessing(item, processor);
      } else if (React.isValidElement(item)) {
        return item;
      } else {
        return processor(item);
      }
    });
  } else {
    return processor(content);
  }
};
